import { PathLayer } from "@deck.gl/layers"
import { UserLocation } from "@nova/schemas"
import { selectLiveUserLocationShares } from "features/entity-caching/flights/selectors"
import {
    selectOpenUserLocationShareIds,
    selectUserLocationSharePathEnabledMap,
} from "plugins/UserLocationShare/state/selectors"
import { userLocationStore } from "features/user-locations/user-location-store"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { userLocationSharePlaybackStore } from "./userLocationSharePlaybackStore"

const USER_LOCATION_PATH_COLOR: [number, number, number] = [30, 109, 169]
const USER_LOCATION_PATH_WIDTH = 3

type UserLocationPathData = { path: [number, number][] }

export const UserLocationPathLayers = ({ data }: { data: UserLocationPathData[] }) => [
    new PathLayer<UserLocationPathData>({
        id: "user-location-paths",
        data,
        pickable: false,
        getPath: (d) => d.path,
        getColor: USER_LOCATION_PATH_COLOR,
        getWidth: USER_LOCATION_PATH_WIDTH,
        widthUnits: "pixels",
    }),
]

export const useUserLocationPathLayer = () => {
    const liveShares = useAppSelector(selectLiveUserLocationShares)
    const pathsByUserId = userLocationStore.useAllPaths()
    const openIds = useAppSelector(selectOpenUserLocationShareIds)
    const playbackByShareId = userLocationSharePlaybackStore.useAll()
    const pathEnabledByShareId = useAppSelector(selectUserLocationSharePathEnabledMap)

    return useMemo(() => {
        const liveData = liveShares
            .filter((share) => share.visible && share.createdBy?.userId)
            .map((share) => pathsByUserId.get(share.createdBy!.userId))
            .filter((path): path is UserLocation[] => !!path?.length)
            .map((path) => ({
                path: path.map((p) => [p.longitude, p.latitude] as [number, number]),
            }))

        const playbackData = openIds
            .filter((id) => pathEnabledByShareId[id] !== false)
            .flatMap((id) => {
                const playback = playbackByShareId.get(id)
                if (!playback?.path.length) return []
                return [
                    {
                        path: playback.path.map(
                            (e) => [e.longitude, e.latitude] as [number, number]
                        ),
                    },
                ]
            })

        return UserLocationPathLayers({ data: [...liveData, ...playbackData] })
    }, [liveShares, pathsByUserId, openIds, playbackByShareId, pathEnabledByShareId])
}
